import { default_property } from "../../schema/investment_property";
import { deep_clone_simple } from "../../constants/utils";
import { YES, NO } from "../../constants/yes_no";
import { mismoBool } from "../mismo_util";
import {
  DISPOSITION_FROM_MISMO,
  OCCUPANCY_FROM_MISMO
} from "../../constants/property_disposition";

/**
 * Investment / Real Estate Owned import.
 *
 * REO is carried as an <ASSET> whose child is <OWNED_PROPERTY> (rather than
 * <ASSET_DETAIL>, which is what a bank account uses). The shape written by
 * LANIS — and produced by POINT's DU export — is:
 *
 *   ASSET
 *     OWNED_PROPERTY
 *       OWNED_PROPERTY_DETAIL   amounts, rent, disposition
 *       PROPERTY
 *         ADDRESS
 *         PROPERTY_DETAIL       estimated value, usage
 *
 * Note: src/mismo/exporters/reo.js describes a DIFFERENT, flatter layout. It is
 * dead code — nothing imports it. The live export is the REO block inside
 * exporters/asset.js, which is what this importer mirrors.
 */

/** Address-based identity for deduping REO. */
function reoKey(obj) {
  const addr = obj && obj.PROPERTY && obj.PROPERTY.ADDRESS;
  if (!addr || !addr.AddressLineText) return null;
  return `${String(addr.AddressLineText).trim().toLowerCase()}|${String(
    addr.PostalCode || ""
  ).trim()}`;
}

/**
 * Create-or-reuse the LANIS investment property for one <OWNED_PROPERTY>.
 *
 * A POINT round trip writes the same property once per borrower, so the same
 * address arrives twice. Dedup on address + ZIP so it lands as one row rather
 * than doubling on the Invest REO page.
 */
export function createReoRecord(obj, context) {
  const { document, reoOwners, reoAssetLabel } = context;
  if (!context.reoByKey) context.reoByKey = {};
  if (!context.reoAssetLabels) context.reoAssetLabels = {};

  const owners = Array.isArray(reoOwners) ? reoOwners.map(String) : [];

  const key = reoKey(obj);
  if (key && context.reoByKey[key]) {
    // Same property seen again (POINT writes one per borrower) — reuse it and
    // union the owners so a jointly-held property shows once as joint.
    const existing = context.reoByKey[key];
    const merged = Array.isArray(existing.owners) ? [...existing.owners] : [];
    owners.forEach(o => {
      if (!merged.includes(o)) merged.push(o);
    });
    merged.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    existing.owners = merged;
    if (reoAssetLabel) context.reoAssetLabels[reoAssetLabel] = existing;
    return { reoProperty: existing };
  }

  const property = deep_clone_simple(default_property);
  property.reported_on_1040 = NO;
  property.owners = [...owners];
  document.investment_properties.push(property);

  if (key) context.reoByKey[key] = property;
  if (reoAssetLabel) context.reoAssetLabels[reoAssetLabel] = property;
  return { reoProperty: property };
}

export function importOwnedPropertyDetail(obj, { reoProperty }) {
  if (!reoProperty) return;

  const {
    OwnedPropertyLienUPBAmount: unpaidBalance,
    OwnedPropertyMaintenanceExpenseAmount: maintenance,
    OwnedPropertyRentalIncomeGrossAmount: grossRent,
    OwnedPropertySubjectIndicator: isSubject,
    OwnedPropertyDispositionStatusType: disposition
  } = obj;

  if (disposition && DISPOSITION_FROM_MISMO[disposition]) {
    reoProperty.disposition_status = DISPOSITION_FROM_MISMO[disposition];
  }

  if (unpaidBalance !== undefined) {
    const parsed = parseFloat(unpaidBalance);
    if (Number.isFinite(parsed)) {
      reoProperty.principal_balance = String(parsed);
    }
  }

  if (grossRent !== undefined) {
    const parsed = parseFloat(grossRent);
    if (Number.isFinite(parsed)) {
      reoProperty.gross_rent_payment = String(parsed);
    }
  }

  // The export writes maintenance as the SUM of taxes, insurance, HOA and
  // Mello-Roos (total_misc_monthly_fees), so the individual components can't be
  // recovered. Put the total in taxes — it is the field that feeds the same
  // downstream calculation — rather than silently dropping the expense and
  // overstating the net rental income.
  if (maintenance !== undefined) {
    const parsed = parseFloat(maintenance);
    if (Number.isFinite(parsed) && parsed > 0) {
      reoProperty.taxes = parsed.toFixed(2);
    }
  }

  reoProperty.is_subject_property = mismoBool(isSubject) ? YES : NO;
}

export function importReoAddress(obj, { reoProperty }) {
  if (!reoProperty) return;

  const {
    AddressLineText: address,
    AddressAdditionalLineText: address2,
    CityName: city,
    StateCode: state,
    PostalCode: zip
  } = obj;

  if (address) reoProperty.property_address = String(address);
  if (address2) reoProperty.property_address_2 = String(address2);
  if (city) reoProperty.property_city = String(city);
  if (state) reoProperty.property_state = String(state);
  if (zip) reoProperty.property_zip = String(zip);
}

export function importReoPropertyDetail(obj, { reoProperty }) {
  if (!reoProperty) return;

  const {
    PropertyEstimatedValueAmount: marketValue,
    PropertyUsageType: intended,
    PropertyCurrentUsageType: current
  } = obj;

  if (marketValue !== undefined) {
    const parsed = parseFloat(marketValue);
    if (Number.isFinite(parsed)) {
      reoProperty.market_value = String(parsed);
    }
  }

  if (intended && OCCUPANCY_FROM_MISMO[intended]) {
    reoProperty.intended_occupancy = OCCUPANCY_FROM_MISMO[intended];
  }
  if (current && OCCUPANCY_FROM_MISMO[current]) {
    reoProperty.current_occupancy = OCCUPANCY_FROM_MISMO[current];
  }
}
